import React, { useMemo } from 'react';
import styled from 'styled-components';
import { nowToIso } from '../../../../../../helpers/StringHelper';
import CompletedMeasures from './completedMeasurers';
import Confirmed from './confirmed';
import RiskMitigation from './riskMitigation';
import Unconfirmed from './unconfirmed';

const Style = styled.div`
  max-width: 100%;

  .cont {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .island {
    background: white;
    box-shadow: 0px 5px 11px rgba(0, 0, 0, 0.18);
    border-radius: 4px;

    .cont-header {
      border-bottom: 1px solid #D1D1D1;
      padding: 21px 23px;

      .last-update {
        margin-bottom: 0px;
        font-size: 14px;
        margin-top: 0px;
      }

      .top {
        display: flex;

        .legend {
          display: flex;
          gap: var(--size-lg);

          .legend-item {
            display: flex;
            gap: 8px;

            .rounded {
              width: 8px;
              height: 8px;
              border-radius: 100%;
            }

            p {
              font-size: 14px;
              font-weight: 500;
              color: #8C8C8C;
              margin-bottom: 0px;
            }
          }

          .toggle { 
            width: var(--size-lg);
            height: var(--size-lg);
            background: var(--color-primary-70);
            cursor: pointer;
            color: white;
            border-radius: 100%;
            text-align: center;
            display: flex;
            flex-direction: column;
            justify-content: center;

            span {
              display: flex;
              justify-content: center;
            }

            &.unactive {
              background: #D4D4D4;
            }
          }
        }

        h1 {
          font-size: 20px;
          flex: 1;
          font-weight: 500;
          margin-bottom: 0px;
        }
      }
      
      label {
        margin-top: 13px;
        margin-bottom: 0px;
        font-weight: 500;
        font-size: var(--font-size-lg);
      }
    }

    &.island-mt {
      margin-top: 20px;
    }

    .cont-body {
      padding: 21px 23px 21px 23px;

      &.unactive {
        max-height: 0px;
        overflow: hidden;
        padding-top: 0px;
        padding-bottom: 0px;
      }
    }
  }

  .buttons {
    display: flex;
    gap: 10px;
    margin-top: 28px;
  }
`;

export default function Transfer({
  events = [],
  value = [],
  onChange = () => { },
  titles = '',
  unlinkedColumns = [],
  linkedColumns = [],
  measures = [],
  inputLabel = [],
  linkToEvent = () => '',
  planningMeasures = [],
  otherPlanningMeasures = [],
  onChangeMeasures = () => { },
  onChangeOtherMeasures = () => { },
  config = {},
}) {
  const eventsValue = useMemo(() => value.filter((v) => v.subject !== 'measure') || [], [value]);
  const measuresValue = useMemo(() => measures.filter((m) => (m.events || []).length), [measures]);

  // Confirmed(are in the events values, not in measures values)
  const confirmed = useMemo(() => {
    let all = events.filter((e) => {
      if (eventsValue.find((v) => v.id === e.id)) {
        return true;
      }
      return eventsValue.includes(e.id);
    })
      .map((e) => ({
        ...e,
        status: (eventsValue.find((v) => v.id === e.id || v === e.id)).status || 'relevant',
      }));

    all = all.map((e) => {
      let isLinked = false;
      measuresValue.forEach((m) => {
        if (Array.isArray(m.events)) {
          if (m.events.includes(e.id || e)) {
            isLinked = true;
            return;
          }
        }
      });
      return { ...e, isLinked };
    });

    const relevant = all.filter((e) => e.status === 'relevant');
    const unrelevant = all.filter((e) => e.status === 'unrelevant');
    const incidents = relevant.filter((e) => e.typeOfEvent === 'incident');
    const correctiveActions = relevant.filter((e) => e.typeOfEvent === 'riskMitigationMeasures');
    const other = relevant.filter((e) => e.typeOfEvent === 'other');
    return { incidents, correctiveActions, other, unrelevant };
  }, [eventsValue, events, measuresValue]);

  // Arent in events value
  const unConfirmed = useMemo(() => {
    const all = events.filter((e) => {
      if (eventsValue.find((v) => v.id === e.id)) {
        return false;
      }
      return !eventsValue.includes(e.id)
    });
    const incidents = all.filter((e) => e.typeOfEvent === 'incident');
    const correctiveActions = all.filter((e) => e.typeOfEvent === 'riskMitigationMeasures');
    const other = all.filter((e) => e.typeOfEvent === 'other');
    return { incidents, correctiveActions, other };
  }, [eventsValue, events]);

  const setAsRelevant = (values) => {
    const newVals = values.map((v) => ({ id: v, status: 'relevant' }));
    onChange([...eventsValue, ...newVals]);
  };

  const setAsUnRelevant = (values) => {
    const newVals = values.map((v) => ({ id: v, status: 'unrelevant' }));
    onChange([...eventsValue, ...newVals]);
  };

  const removeEvents = (values) => {
    const newVals = value.filter((val) => {
      if (values.find((v) => val.id === v || val === v)) {
        return false;
      }
      return true;
    });
    onChange([...newVals]);
  };

  const linkToMeasure = (measure, events) => {
    const isOther = otherPlanningMeasures.find((m) => m.id === measure);
    const isMeasure = planningMeasures.find((m) => m.id === measure);
    onChange(eventsValue);

    if (isOther) {
      onChangeOtherMeasures([
        ...otherPlanningMeasures.filter((m) => m.id !== measure),
        { ...isOther, events: [...(isOther.events || []), ...events] }
      ]);
    } else if (isMeasure) {
      onChangeMeasures([
        ...planningMeasures.filter((m) => m.id !== measure),
        { ...isMeasure, events: [...(isMeasure.events || []), ...events] }
      ]);
    }
  };

  const disconnectEvent = (measureId, eventId) => {
    const isOther = otherPlanningMeasures.find((m) => m.id === measureId);
    const isMeasure = planningMeasures.find((m) => m.id === measureId);

    if (isOther) {
      const filteredMeasures = otherPlanningMeasures.filter((m) => m.id !== measureId);
      onChangeOtherMeasures([...filteredMeasures, { ...isOther, events: isOther.events.filter((evId) => evId !== eventId) }]);
    } else if (isMeasure) {
      const filteredMeasures = planningMeasures.filter((m) => m.id !== measureId);
      onChangeMeasures([...filteredMeasures, { ...isMeasure, events: isMeasure.events.filter((evId) => evId !== eventId) }]);
    }
  };

  const completeMeasure = (measureId) => {
    const isOther = otherPlanningMeasures.find((m) => m.id === measureId);
    const isMeasure = planningMeasures.find((m) => m.id === measureId);

    if (isOther) {
      const filteredMeasures = otherPlanningMeasures.filter((m) => m.id !== measureId);
      onChangeOtherMeasures([...filteredMeasures, { ...isOther, completedAt: nowToIso() }]);
    } else if (isMeasure) {
      const filteredMeasures = planningMeasures.filter((m) => m.id !== measureId);
      onChangeMeasures([...filteredMeasures, { ...isMeasure, completedAt: nowToIso() }]);
    }
  };

  return (
    <Style>
      <Unconfirmed
        data={unConfirmed}
        columns={unlinkedColumns}
        setAsRelevant={setAsRelevant}
        setAsUnRelevant={setAsUnRelevant}
        inputLabel={inputLabel}
      />
      <Confirmed
        data={confirmed}
        columns={linkedColumns}
        removeEvents={removeEvents}
        measures={measures.filter((m) => typeof m.completedAt !== 'string')}
        linkToMeasure={linkToMeasure}
        linkToEvent={linkToEvent}
        inputLabel={config.confirmedEvents ? config.confirmedEvents.label : null}
      />
      <RiskMitigation
        measures={measures}
        measuresValue={measuresValue}
        events={events}
        disconnectEvent={disconnectEvent}
        completeMeasure={completeMeasure}
        linkToEvent={linkToEvent}
        inputLabel={config.confirmedEvents ? config.riskMitigation.label : null}
      />
      <CompletedMeasures
        measures={measures}
        measuresValue={measuresValue}
        events={events}
        linkToEvent={linkToEvent}
      />
    </Style>
  );
}
